import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { menuOptions } from "../lib/callDevices";
import type { UseCallDevicesResult } from "../hooks/useCallDevices";
import { DeviceSelectButton } from "./DeviceSelectButton";
import { SpeakersSection, type SpeakersAudio } from "./SpeakersSection";
import { VoicePathBadge } from "./VoicePathBadge";

interface InCallButtonLook {
  light?: boolean;
  /** 56px variant for the pre-join preview tiles. */
  big?: boolean;
  circle?: boolean;
  small?: boolean;
  compact?: boolean;
  openUp?: boolean;
  /** Bottom sheet instead of a popover (phones). */
  sheet?: boolean;
}

interface InCallMicButtonProps extends InCallButtonLook {
  micOn: boolean;
  onToggleMic: () => void;
  devices: UseCallDevicesResult;
  /** The live call's output controls; left out on a pre-join screen. */
  audio?: SpeakersAudio;
  onOpen?: () => void;
}

/**
 * The in-call mic button: its arrow opens the merged menu — Microphones, then
 * Speakers with the volume (GH-1859). Shared by the call bar and the
 * minimised call widget. In a live call (`audio` given) it also carries the
 * "is my voice getting through?" badge (`VoicePathBadge`).
 */
export function InCallMicButton({ micOn, onToggleMic, devices, audio, onOpen, ...look }: InCallMicButtonProps) {
  const { t } = useTranslation();
  const micLabel = t("call.preJoin.microphone");
  const speakerLabel = t("call.controls.speaker");
  const microphones = useMemo(
    () => menuOptions(devices.microphones, "audioinput", micLabel),
    [devices.microphones, micLabel],
  );
  const speakers = useMemo(
    () => menuOptions(devices.speakers, "audiooutput", speakerLabel),
    [devices.speakers, speakerLabel],
  );

  const button = (
    <DeviceSelectButton
      kind="microphone"
      on={micOn}
      onToggle={onToggleMic}
      devices={microphones}
      deviceId={devices.selectedId("audioinput")}
      onSelectDevice={(id) => devices.select("audioinput", id)}
      onOpen={onOpen}
      sectionLabel={t("call.controls.devicesMicrophones")}
      extraSection={(close) => (
        <SpeakersSection
          speakers={speakers}
          selectedId={devices.selectedId("audiooutput")}
          onSelect={(id) => devices.select("audiooutput", id)}
          audio={audio}
          light={look.light}
          onPicked={close}
        />
      )}
      {...look}
    />
  );
  if (!audio) return button;
  return (
    <div className="relative">
      {button}
      <VoicePathBadge light={look.light} />
    </div>
  );
}

interface InCallCameraButtonProps extends InCallButtonLook {
  cameraOn: boolean;
  onToggleCamera: () => void;
  devices: UseCallDevicesResult;
  onOpen?: () => void;
}

/** The in-call camera button with its own device arrow. */
export function InCallCameraButton({ cameraOn, onToggleCamera, devices, onOpen, ...look }: InCallCameraButtonProps) {
  const { t } = useTranslation();
  const camLabel = t("call.preJoin.camera");
  const cameras = useMemo(
    () => menuOptions(devices.cameras, "videoinput", camLabel),
    [devices.cameras, camLabel],
  );

  return (
    <DeviceSelectButton
      kind="camera"
      on={cameraOn}
      onToggle={onToggleCamera}
      devices={cameras}
      deviceId={devices.selectedId("videoinput")}
      onSelectDevice={(id) => devices.select("videoinput", id)}
      onOpen={onOpen}
      {...look}
    />
  );
}

type PreJoinButtonLook = Pick<InCallButtonLook, "big" | "circle">;

/**
 * The pre-join mic button: the same merged menu as in the call, minus volume
 * and Earpiece / Loudspeaker — nothing is playing yet (GH-1859). Opening it
 * re-reads the devices, since the preview's permission grant may have just
 * revealed their names.
 */
export function PreJoinMicButton({
  micOn,
  onToggleMic,
  devices,
  ...look
}: { micOn: boolean; onToggleMic: () => void; devices: UseCallDevicesResult } & PreJoinButtonLook) {
  return <InCallMicButton micOn={micOn} onToggleMic={onToggleMic} devices={devices} onOpen={devices.refresh} openUp {...look} />;
}

/** The pre-join camera button with its own device arrow. */
export function PreJoinCameraButton({
  cameraOn,
  onToggleCamera,
  devices,
  ...look
}: { cameraOn: boolean; onToggleCamera: () => void; devices: UseCallDevicesResult } & PreJoinButtonLook) {
  return <InCallCameraButton cameraOn={cameraOn} onToggleCamera={onToggleCamera} devices={devices} onOpen={devices.refresh} openUp {...look} />;
}
